// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Formats confidence as a percentage, avoiding floating-point display artifacts.
 * Returns '—' when the value is not a finite number.
 */
function formatConfidence(confidence) {
  const n = Number(confidence);
  if (!Number.isFinite(n)) return '—';
  return `${n}%`;
}

/**
 * Toggles a key in a Set of expanded rationale IDs.
 */
function toggleRationaleKey(prev, key) {
  const next = new Set(prev);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
}

/** Returns true if value is a non-empty array. */
function hasItems(arr) {
  return Array.isArray(arr) && arr.length > 0;
}

/**
 * Normalizes importResults to the addBeer response shape so the view supports
 * both new (addedCount, addedBeers, ...) and legacy (successfulBeers, ...) shapes.
 *
 * TODO: Remove legacy shape support once all callers use the addBeer response
 * (addedCount, addedBeers, updatedBeers, duplicatedBeers, errors). Then simplify
 * this to only accept the new shape and drop the successfulBeers/rejectedBeers/
 * reviewBeers branch.
 */
function normalizeImportResults(importResults) {
  if (!importResults) {
    return {
      addedCount: 0,
      updatedCount: 0,
      duplicatedCount: 0,
      errorCount: 0,
      addedBeers: [],
      updatedBeers: [],
      duplicatedBeers: [],
      errors: [],
      status: '',
    };
  }

  // Already new shape
  if (
    typeof importResults.addedCount === 'number' &&
    Array.isArray(importResults.addedBeers)
  ) {
    return {
      addedCount: importResults.addedCount ?? 0,
      updatedCount: importResults.updatedCount ?? 0,
      duplicatedCount: importResults.duplicatedCount ?? 0,
      errorCount: importResults.errorCount ?? 0,
      addedBeers: importResults.addedBeers ?? [],
      updatedBeers: importResults.updatedBeers ?? [],
      duplicatedBeers: importResults.duplicatedBeers ?? [],
      errors: importResults.errors ?? [],
      status: importResults.status ?? '',
    };
  }

  // Legacy shape: successfulBeers, updatedBeers (string[]), errors (string[]), rejectedBeers, reviewBeers
  const addedBeers = (importResults.successfulBeers ?? []).map((name) => ({
    id: '',
    name: String(name),
  }));
  const updatedBeers = (importResults.updatedBeers ?? []).map((name) => ({
    inputBeer: { name: String(name) },
    updatedBeer: { id: '', name: String(name) },
  }));
  const rawErrors = importResults.errors ?? [];
  const errors = rawErrors.map((e) =>
    typeof e === 'object' && e !== null && 'message' in e
      ? { id: e.id ?? '', name: e.name ?? '', message: e.message ?? '' }
      : { id: '', name: '', message: String(e) },
  );
  const rejected = (importResults.rejectedBeers ?? []).map((r) => ({
    matchType: 'AI',
    confidence: r.confidence ?? 0,
    rationale: r.rationale ?? '',
    inputBeer: { name: r.name ?? '' },
    matchedBeer: { id: r.matchedBeerId ?? '', name: r.matchedBeerName ?? '' },
  }));
  const review = (importResults.reviewBeers ?? []).map((r) => ({
    matchType: 'AI',
    confidence: r.confidence ?? 0,
    rationale: r.rationale ?? '',
    inputBeer: { name: r.name ?? '' },
    matchedBeer: { id: r.matchedBeerId ?? '', name: r.matchedBeerName ?? '' },
  }));
  const duplicatedBeers = [...rejected, ...review];

  return {
    addedCount: addedBeers.length,
    updatedCount: updatedBeers.length,
    duplicatedCount: duplicatedBeers.length,
    errorCount: errors.length,
    addedBeers,
    updatedBeers,
    duplicatedBeers,
    errors,
    status: importResults.status ?? '',
  };
}

/** Returns true if there are no results to show. */
function hasNoImportResults(normalized) {
  return (
    normalized.addedCount === 0 &&
    normalized.updatedCount === 0 &&
    normalized.duplicatedCount === 0 &&
    normalized.errorCount === 0
  );
}

/**
 * Button that toggles rationale visibility for a single duplicated row.
 */
function RationaleToggleButton({ itemKey, showRationale, toggleRationale }) {
  const handleClick = useCallback(() => {
    toggleRationale(itemKey);
  }, [itemKey, toggleRationale]);
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={handleClick}
      sx={{ textTransform: 'none' }}
    >
      {showRationale ? 'Hide rationale' : 'Rationale'}
    </Button>
  );
}

RationaleToggleButton.propTypes = {
  itemKey: PropTypes.string.isRequired,
  showRationale: PropTypes.bool.isRequired,
  toggleRationale: PropTypes.func.isRequired,
};

/** Wraps table content to keep JSX nesting under the lint limit. */
function SummaryTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell align="center" sx={{ fontWeight: 600 }}>Added</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>Updated</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>Duplicated</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>Errors</TableCell>
      </TableRow>
    </TableHead>
  );
}

function SummaryTableBody({ addedCount, updatedCount, duplicatedCount, errorCount }) {
  return (
    <TableBody>
      <TableRow>
        <TableCell align="center">{addedCount}</TableCell>
        <TableCell align="center">{updatedCount}</TableCell>
        <TableCell align="center">{duplicatedCount}</TableCell>
        <TableCell align="center">{errorCount}</TableCell>
      </TableRow>
    </TableBody>
  );
}

SummaryTableBody.propTypes = {
  addedCount: PropTypes.number.isRequired,
  updatedCount: PropTypes.number.isRequired,
  duplicatedCount: PropTypes.number.isRequired,
  errorCount: PropTypes.number.isRequired,
};

/** Summary table: one row with Added | Updated | Duplicated | Errors counts. */
function SummaryTable({ addedCount, updatedCount, duplicatedCount, errorCount }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small" aria-label="Import summary">
        <SummaryTableHead />
        <SummaryTableBody
          addedCount={addedCount}
          updatedCount={updatedCount}
          duplicatedCount={duplicatedCount}
          errorCount={errorCount}
        />
      </Table>
    </TableContainer>
  );
}

SummaryTable.propTypes = {
  addedCount: PropTypes.number.isRequired,
  updatedCount: PropTypes.number.isRequired,
  duplicatedCount: PropTypes.number.isRequired,
  errorCount: PropTypes.number.isRequired,
};

/** Wraps table in container to keep section JSX nesting under the lint limit. */
function SectionTable({ 'aria-label': ariaLabel, children }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={ariaLabel}>
        {children}
      </Table>
    </TableContainer>
  );
}

SectionTable.propTypes = {
  'aria-label': PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function AddedBeersTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
      </TableRow>
    </TableHead>
  );
}

function AddedBeersTableBody({ addedBeers }) {
  const emptyRow = (
    <TableRow>
      <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
        None
      </TableCell>
    </TableRow>
  );
  const rows = addedBeers.map((beer) => (
    <TableRow key={`added-${beer.id}-${beer.name}`}>
      <TableCell>{beer.name || '—'}</TableCell>
      <TableCell>{beer.id || '—'}</TableCell>
    </TableRow>
  ));
  return (
    <TableBody>
      {addedBeers.length === 0 ? emptyRow : rows}
    </TableBody>
  );
}

AddedBeersTableBody.propTypes = {
  addedBeers: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  ).isRequired,
};

/** Accordion section with a table of added beers (id, name). */
function AddedBeersSection({ addedBeers }) {
  return (
    <Accordion defaultExpanded={hasItems(addedBeers)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
          Added Beers ({addedBeers.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <SectionTable aria-label="Added beers">
          <AddedBeersTableHead />
          <AddedBeersTableBody addedBeers={addedBeers} />
        </SectionTable>
      </AccordionDetails>
    </Accordion>
  );
}

AddedBeersSection.propTypes = {
  addedBeers: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  ).isRequired,
};

function UpdatedBeersTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>Name (input)</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Name (saved)</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
      </TableRow>
    </TableHead>
  );
}

function UpdatedBeersTableBody({ updatedBeers }) {
  const emptyRow = (
    <TableRow>
      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
        None
      </TableCell>
    </TableRow>
  );
  const rows = updatedBeers.map((row) => {
    const rowKey = row.updatedBeer?.id ?? `updated-${row.inputBeer?.name ?? ''}-${row.updatedBeer?.name ?? ''}`;
    return (
      <TableRow key={rowKey}>
        <TableCell>{row.inputBeer?.name ?? '—'}</TableCell>
        <TableCell>{row.updatedBeer?.name ?? '—'}</TableCell>
        <TableCell>{row.updatedBeer?.id ?? '—'}</TableCell>
      </TableRow>
    );
  });
  return (
    <TableBody>
      {updatedBeers.length === 0 ? emptyRow : rows}
    </TableBody>
  );
}

UpdatedBeersTableBody.propTypes = {
  updatedBeers: PropTypes.arrayOf(
    PropTypes.shape({ inputBeer: PropTypes.object, updatedBeer: PropTypes.object }),
  ).isRequired,
};

/** Accordion section with a table of updated beers (input name, updated name/id). */
function UpdatedBeersSection({ updatedBeers }) {
  return (
    <Accordion defaultExpanded={hasItems(updatedBeers)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600, color: 'info.main' }}>
          Updated Beers ({updatedBeers.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <SectionTable aria-label="Updated beers">
          <UpdatedBeersTableHead />
          <UpdatedBeersTableBody updatedBeers={updatedBeers} />
        </SectionTable>
      </AccordionDetails>
    </Accordion>
  );
}

UpdatedBeersSection.propTypes = {
  updatedBeers: PropTypes.arrayOf(
    PropTypes.shape({
      inputBeer: PropTypes.object,
      updatedBeer: PropTypes.object,
    }),
  ).isRequired,
};

/** Stable key for a duplicated beer row (no array index). */
function getDuplicatedBeerKey(beer) {
  const inputName = beer.inputBeer?.name ?? '';
  const matchedId = beer.matchedBeer?.id ?? '';
  const matchedName = beer.matchedBeer?.name ?? '';
  const rationale = (beer.rationale ?? '').slice(0, 40);
  return `dupe-${matchedId}-${inputName}-${matchedName}-${rationale}`;
}

function DuplicatedBeersTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>Input name</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Matched beer</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Rationale</TableCell>
      </TableRow>
    </TableHead>
  );
}

function DuplicatedBeerRow({ beer, itemKey, showRationale, toggleRationale }) {
  const rationaleCell = beer.rationale ? (
    <>
      <RationaleToggleButton
        itemKey={itemKey}
        showRationale={showRationale}
        toggleRationale={toggleRationale}
      />
      {showRationale && (
        <Typography
          variant="body2"
          sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
        >
          {beer.rationale}
        </Typography>
      )}
    </>
  ) : (
    '—'
  );
  return (
    <TableRow>
      <TableCell>{beer.inputBeer?.name ?? '—'}</TableCell>
      <TableCell>{beer.matchedBeer?.name ?? beer.matchedBeer?.id ?? '—'}</TableCell>
      <TableCell>{beer.matchType ?? '—'}</TableCell>
      <TableCell>{formatConfidence(beer.confidence)}</TableCell>
      <TableCell>{rationaleCell}</TableCell>
    </TableRow>
  );
}

DuplicatedBeerRow.propTypes = {
  beer: PropTypes.shape({
    inputBeer: PropTypes.object,
    matchedBeer: PropTypes.object,
    matchType: PropTypes.string,
    confidence: PropTypes.number,
    rationale: PropTypes.string,
  }).isRequired,
  itemKey: PropTypes.string.isRequired,
  showRationale: PropTypes.bool.isRequired,
  toggleRationale: PropTypes.func.isRequired,
};

function DuplicatedBeersTableBody({
  duplicatedBeers,
  expandedRationale,
  toggleRationale,
}) {
  const emptyRow = (
    <TableRow>
      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
        None
      </TableCell>
    </TableRow>
  );
  const rows = duplicatedBeers.map((beer) => {
    const itemKey = getDuplicatedBeerKey(beer);
    const showRationale = expandedRationale.has(itemKey);
    return (
      <DuplicatedBeerRow
        key={itemKey}
        beer={beer}
        itemKey={itemKey}
        showRationale={showRationale}
        toggleRationale={toggleRationale}
      />
    );
  });
  return (
    <TableBody>
      {duplicatedBeers.length === 0 ? emptyRow : rows}
    </TableBody>
  );
}

DuplicatedBeersTableBody.propTypes = {
  duplicatedBeers: PropTypes.arrayOf(
    PropTypes.shape({
      matchType: PropTypes.string,
      confidence: PropTypes.number,
      rationale: PropTypes.string,
      inputBeer: PropTypes.object,
      matchedBeer: PropTypes.object,
    }),
  ).isRequired,
  expandedRationale: PropTypes.instanceOf(Set).isRequired,
  toggleRationale: PropTypes.func.isRequired,
};

/** Accordion section with a table of duplicated beers (match type, confidence, rationale, names). */
function DuplicatedBeersSection({
  duplicatedBeers,
  expandedRationale,
  toggleRationale,
}) {
  return (
    <Accordion defaultExpanded={hasItems(duplicatedBeers)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600, color: 'warning.main' }}>
          Duplicated Beers ({duplicatedBeers.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <SectionTable aria-label="Duplicated beers">
          <DuplicatedBeersTableHead />
          <DuplicatedBeersTableBody
            duplicatedBeers={duplicatedBeers}
            expandedRationale={expandedRationale}
            toggleRationale={toggleRationale}
          />
        </SectionTable>
      </AccordionDetails>
    </Accordion>
  );
}

DuplicatedBeersSection.propTypes = {
  duplicatedBeers: PropTypes.arrayOf(
    PropTypes.shape({
      matchType: PropTypes.string,
      confidence: PropTypes.number,
      rationale: PropTypes.string,
      inputBeer: PropTypes.object,
      matchedBeer: PropTypes.object,
    }),
  ).isRequired,
  expandedRationale: PropTypes.instanceOf(Set).isRequired,
  toggleRationale: PropTypes.func.isRequired,
};

function ErrorsTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>Beer / ID</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
      </TableRow>
    </TableHead>
  );
}

function ErrorsTableBody({ errors }) {
  const emptyRow = (
    <TableRow>
      <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
        None
      </TableCell>
    </TableRow>
  );
  const rows = errors.map((err) => {
    const rowKey = err.id || `error-${err.name ?? ''}-${err.message ?? ''}`;
    return (
      <TableRow key={rowKey}>
        <TableCell>{err.name || err.id || '—'}</TableCell>
        <TableCell>
          <Typography component="span" color="error">
            {err.message || '—'}
          </Typography>
        </TableCell>
      </TableRow>
    );
  });
  return (
    <TableBody>
      {errors.length === 0 ? emptyRow : rows}
    </TableBody>
  );
}

ErrorsTableBody.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, message: PropTypes.string }),
  ).isRequired,
};

/** Accordion section with a table of errors (name, message). */
function ErrorsSection({ errors }) {
  return (
    <Accordion defaultExpanded={hasItems(errors)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600, color: 'error.main' }}>
          Errors ({errors.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <SectionTable aria-label="Import errors">
          <ErrorsTableHead />
          <ErrorsTableBody errors={errors} />
        </SectionTable>
      </AccordionDetails>
    </Accordion>
  );
}

ErrorsSection.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, message: PropTypes.string }),
  ).isRequired,
};

/**
 * ImportSummaryView
 * Displays import results: a summary row (Added / Updated / Duplicated / Errors)
 * and expandable sections with tables for each category.
 */
function ImportSummaryView({ importResults = null, hideEmptyAlert = false }) {
  const [expandedRationale, setExpandedRationale] = useState(new Set());

  const toggleRationale = useCallback((key) => {
    setExpandedRationale((prev) => toggleRationaleKey(prev, key));
  }, []);

  const normalized = useMemo(
    () => normalizeImportResults(importResults),
    [importResults],
  );

  const status = normalized.status || 'Import completed';
  const showEmptyMessage = hasNoImportResults(normalized) && !hideEmptyAlert;

  return (
    <Box>
      <DialogContentText id="find-beer-dialog-description" sx={{ mb: 2 }}>
        {status}
      </DialogContentText>

      <SummaryTable
        addedCount={normalized.addedCount}
        updatedCount={normalized.updatedCount}
        duplicatedCount={normalized.duplicatedCount}
        errorCount={normalized.errorCount}
      />

      <Box sx={{ '& .MuiAccordion-root': { boxShadow: 'none', '&:before': { display: 'none' } } }}>
        <AddedBeersSection addedBeers={normalized.addedBeers} />
        <UpdatedBeersSection updatedBeers={normalized.updatedBeers} />
        <DuplicatedBeersSection
          duplicatedBeers={normalized.duplicatedBeers}
          expandedRationale={expandedRationale}
          toggleRationale={toggleRationale}
        />
        <ErrorsSection errors={normalized.errors} />
      </Box>

      {showEmptyMessage && (
        <Alert severity="info" variant="filled" elevation={6} sx={{ mt: 2 }}>
          No beers were imported. Please check the scraped beer data and try again.
        </Alert>
      )}
    </Box>
  );
}

const errorShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  message: PropTypes.string,
});

const addedBeerShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
});

const updatedBeerRowShape = PropTypes.shape({
  inputBeer: PropTypes.object,
  updatedBeer: PropTypes.object,
});

const duplicatedBeerShape = PropTypes.shape({
  matchType: PropTypes.string,
  confidence: PropTypes.number,
  rationale: PropTypes.string,
  inputBeer: PropTypes.object,
  matchedBeer: PropTypes.object,
});

ImportSummaryView.propTypes = {
  hideEmptyAlert: PropTypes.bool,
  importResults: PropTypes.oneOfType([
    PropTypes.shape({
      status: PropTypes.string,
      addedCount: PropTypes.number,
      updatedCount: PropTypes.number,
      duplicatedCount: PropTypes.number,
      errorCount: PropTypes.number,
      addedBeers: PropTypes.arrayOf(addedBeerShape),
      updatedBeers: PropTypes.arrayOf(updatedBeerRowShape),
      duplicatedBeers: PropTypes.arrayOf(duplicatedBeerShape),
      errors: PropTypes.arrayOf(errorShape),
    }),
    PropTypes.shape({
      status: PropTypes.string,
      successfulBeers: PropTypes.arrayOf(PropTypes.string),
      updatedBeers: PropTypes.arrayOf(PropTypes.string),
      errors: PropTypes.array,
      rejectedBeers: PropTypes.array,
      reviewBeers: PropTypes.array,
    }),
  ]),
};

export default ImportSummaryView;
